'use client';

import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useDrawingStore } from '@/features/drawing/model';
import {
  TEXT_BLOCK_CONSTRAINTS,
  useTextBlockDrag,
  useTextBlockResize,
  TextBlockItem,
  type TextBlockPosition,
} from './text-blocks';

interface TextBlocksOverlayProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function TextBlocksOverlay({ containerRef }: TextBlocksOverlayProps) {
  // Store selectors
  const textBlocks = useDrawingStore((state) => state.textBlocks.blocks);
  const activeBlockId = useDrawingStore(
    (state) => state.textBlocks.activeBlockId
  );
  const canvasTransform = useDrawingStore((state) => state.canvas.transform);
  const canvasWidth = useDrawingStore((state) => state.canvas.width);
  const canvasHeight = useDrawingStore((state) => state.canvas.height);
  const tool = useDrawingStore((state) => state.tool);
  const updateTextBlock = useDrawingStore((state) => state.updateTextBlock);
  const deleteTextBlock = useDrawingStore((state) => state.deleteTextBlock);
  const setActiveTextBlock = useDrawingStore(
    (state) => state.setActiveTextBlock
  );

  // Local state
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);
  const [isOverDraggableArea, setIsOverDraggableArea] = useState<string | null>(
    null
  );
  const editableRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Drag hook
  const {
    potentialDrag,
    handleDragStart,
    handleTouchDragStart,
    startDragFromPotential,
    setPotentialDrag,
  } = useTextBlockDrag({
    activeBlockId,
    containerRef,
    canvasTransform,
    updateTextBlock,
  });

  // Resize hook
  const { handleResizeStart, handleTouchResizeStart } = useTextBlockResize({
    textBlocks,
    canvasTransform,
    updateTextBlock,
  });

  // Deactivate text block when switching away from Text tool
  useEffect(() => {
    if (tool !== 'text' && activeBlockId) {
      setActiveTextBlock(null);
    }
  }, [tool, activeBlockId, setActiveTextBlock]);

  // Clear hover state when block is deactivated
  useEffect(() => {
    if (!activeBlockId) {
      setHoveredBlockId(null);
    }
  }, [activeBlockId]);

  // Convert canvas coordinates to screen coordinates
  const canvasToScreen = useCallback(
    (canvasX: number, canvasY: number) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return { x: 0, y: 0 };

      const centerX = containerRect.width / 2;
      const centerY = containerRect.height / 2;

      const relativeX =
        (canvasX - canvasWidth / 2) * canvasTransform.zoom +
        canvasTransform.pan.x;
      const relativeY =
        (canvasY - canvasHeight / 2) * canvasTransform.zoom +
        canvasTransform.pan.y;

      return {
        x: relativeX + centerX + containerRect.left,
        y: relativeY + centerY + containerRect.top,
      };
    },
    [canvasTransform, canvasWidth, canvasHeight, containerRef]
  );

  // Handle text input
  const handleTextInput = useCallback(
    (blockId: string, e: React.FormEvent<HTMLDivElement>) => {
      const text = (e.currentTarget as HTMLDivElement).innerText;
      if (text.length > TEXT_BLOCK_CONSTRAINTS.MAX_CHARS) {
        e.preventDefault();
        return;
      }
      updateTextBlock(blockId, { text });
    },
    [updateTextBlock]
  );

  // Measure and update block.width for auto-mode blocks
  const textBlocksKey = useMemo(
    () =>
      textBlocks.map((b) => `${b.id}:${b.text}:${b.isManualWidth}`).join(','),
    [textBlocks]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentZoom = canvasTransform.zoom;
      const currentTextBlocks = useDrawingStore.getState().textBlocks.blocks;

      currentTextBlocks.forEach((block) => {
        if (block.isManualWidth) return;
        if (block.text.length === 0) return;

        const el = editableRefs.current.get(block.id);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        if (rect.width === 0) return;

        const measuredWidthCanvas =
          rect.width / currentZoom + TEXT_BLOCK_CONSTRAINTS.MEASURE_PADDING;

        if (Math.abs(measuredWidthCanvas - block.width) > 1) {
          updateTextBlock(block.id, {
            width: Math.max(
              TEXT_BLOCK_CONSTRAINTS.MIN_AUTO_WIDTH,
              measuredWidthCanvas
            ),
          });
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [textBlocksKey, updateTextBlock, canvasTransform.zoom]);

  // Handle block activation
  const handleBlockClick = useCallback(
    (e: React.MouseEvent, blockId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setActiveTextBlock(blockId);
    },
    [setActiveTextBlock]
  );

  // Handle delete
  const handleDelete = useCallback(
    (blockId: string) => {
      deleteTextBlock(blockId);
    },
    [deleteTextBlock]
  );

  // Auto-focus contenteditable when block becomes active
  useEffect(() => {
    if (!activeBlockId) return;

    const el = editableRefs.current.get(activeBlockId);
    if (!el) return;

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    const focusElement = () => {
      if (el.isConnected && el.contentEditable === 'true') {
        el.focus({ preventScroll: true });

        try {
          const range = document.createRange();
          const selection = window.getSelection();

          if (selection && el.childNodes.length > 0) {
            const lastNode = el.childNodes[el.childNodes.length - 1];
            if (lastNode && lastNode.isConnected) {
              range.selectNodeContents(lastNode);
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          } else if (selection) {
            range.selectNodeContents(el);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } catch {
          // Ignore selection errors
        }
      }
    };

    if (isMobile) {
      focusElement();
      const timeoutId = setTimeout(focusElement, 100);
      return () => clearTimeout(timeoutId);
    } else {
      const timeoutId = setTimeout(focusElement, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [activeBlockId]);

  // Memoize block positions
  const blockPositions: TextBlockPosition[] = useMemo(() => {
    return textBlocks.map((block) => {
      const screenPos = canvasToScreen(block.x, block.y);
      return {
        ...block,
        screenX: screenPos.x,
        screenY: screenPos.y,
        scaledWidth: block.width * canvasTransform.zoom,
        scaledHeight: block.height * canvasTransform.zoom,
      };
    });
  }, [textBlocks, canvasTransform.zoom, canvasToScreen]);

  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 20 }}>
      {blockPositions.map((block) => {
        const isActive = activeBlockId === block.id;
        const isHovered = hoveredBlockId === block.id;
        const showHoverEffect = !isActive && tool === 'text' && isHovered;
        const isMobile =
          typeof window !== 'undefined' && window.innerWidth < 768;

        return (
          <TextBlockItem
            key={block.id}
            block={block}
            isActive={isActive}
            isHovered={isHovered}
            showHoverEffect={showHoverEffect}
            isMobile={isMobile}
            isOverDraggableArea={isOverDraggableArea === block.id}
            zoom={canvasTransform.zoom}
            textBlocks={textBlocks}
            tool={tool}
            editableRefs={editableRefs}
            onBlockClick={handleBlockClick}
            onDelete={handleDelete}
            onTextInput={handleTextInput}
            onDragStart={handleDragStart}
            onTouchDragStart={handleTouchDragStart}
            onResizeStart={handleResizeStart}
            onTouchResizeStart={handleTouchResizeStart}
            setHoveredBlockId={setHoveredBlockId}
            setIsOverDraggableArea={setIsOverDraggableArea}
            potentialDrag={potentialDrag}
            setPotentialDrag={setPotentialDrag}
            startDragFromPotential={startDragFromPotential}
          />
        );
      })}
    </div>
  );
}
